/**
 * The environment as YAML on the repository sheet (YAG-1107). The server
 * renders it from the draft or the Working revision; saving it declares the
 * environment and runs setup on it, as the form's Run does. Reset reads the
 * repository again and leaves the declared text behind.
 */
import type { QaEnvironmentRevision, QaEnvironmentView } from "@yagni/shared";
import { YamlEditor, type YamlEditorError } from "./YamlEditor.js";

export type EnvironmentYamlView = QaEnvironmentView["yaml"];

export type EnvironmentYamlError = YamlEditorError;

/** The part of a revision the origin line reads. */
type YamlRevision = Pick<QaEnvironmentRevision, "id" | "revision" | "declaredBy">;

export function yamlOriginLine(
  yaml: EnvironmentYamlView,
  revisions: (YamlRevision | null | undefined)[],
): string {
  const present = revisions.filter((item): item is YamlRevision => Boolean(item));
  const revision = present.find((item) => item.id === yaml.revisionId) ?? present[0];
  if (yaml.origin === "declared")
    return `Declared by ${revision?.declaredBy?.name ?? "a person"}${
      revision ? `, revision ${revision.revision}` : ""
    }.`;
  if (yaml.origin === "person")
    return `Edited on the form${revision ? `, revision ${revision.revision}` : ""}.`;
  return "Inferred from the repository. Edit and save to declare it.";
}

export interface EnvironmentYamlProps {
  yaml: EnvironmentYamlView;
  /** The draft and the Working revision, the newest first. */
  revisions: (YamlRevision | null | undefined)[];
  /** A request or a run is in flight. */
  busy: boolean;
  error: EnvironmentYamlError | null;
  onSave: (text: string) => Promise<boolean>;
  onReset: () => void;
  /** The line the latest failed step came from, marked in the gutter. */
  markLine?: number | null;
  /** A step's "line N" asked to see this line. */
  reveal?: { line: number; at: number } | null;
}

export function EnvironmentYaml({
  yaml,
  revisions,
  busy,
  error,
  onSave,
  onReset,
  markLine,
  reveal,
}: EnvironmentYamlProps) {
  return (
    <YamlEditor
      title="Environment"
      fileName=".yagni/environment.yml"
      originLine={yamlOriginLine(yaml, revisions)}
      text={yaml.text}
      revision={yaml.revisionId}
      busy={busy}
      error={error}
      saveLabel="Save and run"
      onSave={onSave}
      reset={yaml.origin === "declared" ? { label: "Reset to inferred", onReset } : null}
      markLine={markLine}
      reveal={reveal}
      testIdPrefix="environment-yaml"
    />
  );
}
